import type { ReactNode } from 'react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useDismissableSurface } from './dismissable-surface'

/**
 * An overlay that slides in from the right edge and sits on top of the page —
 * the thing `DetailPane`'s own doc comment reserved this name for.
 *
 * **Overlay, not inline.** The opposite tradeoff from `DetailPane`: a Drawer
 * covers what's behind it, so it is for work that does not need the rest of
 * the page visible while it happens — starting something new (Add Asset, New
 * Workspace, New Team), not editing a record you're comparing against its
 * neighbours. That second case is exactly what `DetailPane` stayed non-modal
 * for; this is its modal counterpart, not a replacement.
 *
 * Consequences of being modal, all deliberate:
 *  - `trapFocus: true` — the page behind it is not meant to be reachable while
 *    it's open, the same reasoning as `ColumnFilter`'s popover.
 *  - `role="dialog"` + `aria-modal="true"`: the claim non-modal `DetailPane`
 *    explicitly avoids making.
 *  - a backdrop that closes on click, like the mobile sidebar overlay.
 *
 * Both of those are specific to being *modal*, which is the default but not
 * the only mode: `modal={false}` drops the backdrop and the focus trap and
 * swaps `aria-modal` for `role="complementary"` — a third point on the same
 * modal-vs-inline line `DetailPane`'s own comment draws, for a surface that
 * must stay beside the page rather than over it (e.g. dragging something
 * from the page onto a card inside the Drawer, which a backdrop would
 * intercept and a focus trap would make impossible to reach in the first
 * place).
 *
 * Conditionally rendered rather than kept mounted for an exit transition —
 * same simplification `DetailPane` already makes. Holds no state of its own;
 * which form is open belongs to whichever Presenter already owns that.
 */
export const Drawer = ({
  open,
  title,
  closeLabel,
  onClose,
  modal = true,
  children,
}: {
  open: boolean
  title: string
  closeLabel: string
  onClose: () => void
  /** Defaults to `true` — every existing caller keeps today's modal behaviour. */
  modal?: boolean
  children: ReactNode
}) => {
  const surfaceRef = useDismissableSurface({ active: open, onDismiss: onClose, trapFocus: modal })

  if (!open) return null

  return (
    <>
      {modal && <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />}
      <div
        ref={surfaceRef}
        role={modal ? 'dialog' : 'complementary'}
        aria-modal={modal ? 'true' : undefined}
        aria-label={title}
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-shell-surface shadow-xl"
      >
        <div className="flex items-center justify-between gap-2 border-b border-shell-border px-4 py-3">
          <h2 className="text-base font-semibold text-shell-ink">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="-m-1 rounded p-1 text-shell-ink-muted hover:text-shell-ink"
          >
            <XMarkIcon className="size-5" aria-hidden="true" />
            <span className="sr-only">{closeLabel}</span>
          </button>
        </div>
        {/* data-focus-scope: focus lands in the content, not on the close button. */}
        <div data-focus-scope className="flex-1 overflow-y-auto px-4 py-4">
          {children}
        </div>
      </div>
    </>
  )
}
